import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { earnSummaryBunja } from '@flipster/features/src/shared/earn/state';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { Link } from '@flipster/ui';
import ChevronRightIcon from '@flipster/ui/assets/svgs/chevron-right.svg';
import {
  BitcoinCurrency,
  EthereumCurrency,
  PusdtCurrency,
  Usd1Currency,
  UsdtCurrency,
} from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';

const Faq = () => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const getBaseBalanceCap = (currency: string) => {
    return earnSummary?.currencies.find((c) => c.currency === currency)?.aprs.find((apr) => apr.type === 'BASE')
      ?.balanceCapInUsdt;
  };
  const usdtBaseBalanceCap = getBaseBalanceCap(UsdtCurrency);
  const pusdtBaseBalanceCap = getBaseBalanceCap(PusdtCurrency);
  const usd1BaseBalanceCap = getBaseBalanceCap(Usd1Currency);
  const btcBaseBalanceCap = getBaseBalanceCap(BitcoinCurrency);
  const ethBaseBalanceCap = getBaseBalanceCap(EthereumCurrency);

  const contents = [
    {
      title: 'How does it work?',
      desc: (
        <p>
          Flipster Earn lets you earn daily rewards by depositing supported assets like USDE, BTC, ETH, USD1 and USDT.
          For standard Earn, there are no lock-ins or complex procedures—you continue to enjoy full trading liquidity
          while earning rewards. Additionally, users can opt into Premium Earn, which offers higher reward in exchange
          for lock-up of USDT deposits.
        </p>
      ),
    },
    {
      title: 'How are my daily earnings determined?',
      desc: (
        <div>
          <p className={css({ mb: 12 })}>
            Your daily earnings are based on the average wallet balance of each supported asset, measured from 24 random
            snapshots taken between 00:00 and 23:59 UTC. The Annual Percentage Rate (APR) is calculated by multiplying
            the daily rate by 365, and may vary daily based on market conditions.
          </p>
          <ul>
            <li>• For USDE, there is no earn limit.</li>
            <li>
              • For USDT in Standard Earn, only balances up to {numberFormatter(usdtBaseBalanceCap)} USDT are eligible.
            </li>
            <li>
              • For USDT in Premium Earn, only balances up to {numberFormatter(pusdtBaseBalanceCap)} USDT are eligible.
            </li>
            <li>
              • For USD1, only balances up to {numberFormatter(usd1BaseBalanceCap)} USDT equivalent per asset are
              eligible.
            </li>
            <li>
              • For BTC, only balances up to {numberFormatter(btcBaseBalanceCap)} USDT equivalent per asset are
              eligible.
            </li>
            <li>
              • For ETH, only balances up to {numberFormatter(ethBaseBalanceCap)} USDT equivalent per asset are
              eligible.
            </li>
          </ul>
          <br />
          <span className={css({ textStyle: 'Text/M/Strong' })}>Note:</span>
          <br />
          <ul>
            <li>• Earn limit is shared between Premium Earn and USDT.</li>
            <li>
              • For full terms, please refer to our
              <Link
                className={css({ textStyle: 'Text/M/Underline' })}
                href={zendesk.getUrl('EarnCampaign')}
                external
                opener
              >
                terms and conditions.
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: 'When will I receive my rewards?',
      desc: (
        <div>
          Eligible rewards are distributed in USDT or USDE to your Flipster account within 1 business day of the daily
          snapshot at 00:00 UTC.
          <br />
          <br />
          <ul>
            <li>• Standard Earn rewards will appear directly in your wallet.</li>
            <li>
              • Premium Earn rewards are also paid in USDT, but are automatically added to your locked Premium Earn
              balance, compounding your earnings.
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: 'What is USDT Premium?',
      desc: (
        <p>
          Premium Earn lets you earn more rewards on your asset by letting Flipster put it to work. You can deposit and
          withdraw in the specified currency anytime, with funds staying available for trading perpetual swap product.
          <br />
          <br />
          You can withdraw your funds anytime and they will be returned back to your original currency as soon as the
          processing time completes.
        </p>
      ),
    },
    {
      title: 'What is USDE Earn?',
      desc: (
        <div>
          <p>
            USDE Earn lets you earn reward without lock-in, allowing you to trade while earning.
            <br />
            <br />
            You can get USDE by minting with USDT/USDC on Flipster (take about 30m) or by depositing USDE from another
            exchange or wallet.
            <br />
            <br />
            Rewards come from a promotional program between Ethena and Flipster, and the base APR is updated weekly.
          </p>
          <br />
          <span className={css({ textStyle: 'Text/M/Strong' })}>Note:</span>
          <br />
          <ul>
            <li>
              • A 0.07% service fee applies to both minting and redeeming. An additional internal processing gas fee may
              also be deducted.
            </li>
            <li>• Minting and redemption involve withdrawals and deposits to/from Ethena’s wallet.</li>
            <li>
              • For full terms, please refer to our
              <Link
                className={css({ textStyle: 'Text/M/Underline' })}
                href={zendesk.getUrl('USDeEarnTerms')}
                external
                opener
              >
                terms and conditions.
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <section>
      <h2 className={css({ textStyle: 'Title/M', color: 'content.default.level-4' })}>FAQ</h2>
      <div className={flex({ direction: 'column', gap: 8, mt: 16 })}>
        {contents.map((content) => (
          <details key={content.title} className={css({ bg: 'background.default.level-1', rounded: 8 })}>
            <summary
              className={flex({
                justify: 'space-between',
                align: 'center',
                py: 16,
                px: 20,
                cursor: 'pointer',
                textStyle: 'Text/L/Strong',
                color: 'content.default.level-4',
                'details[open] &': { pb: 0 },
              })}
              role='button'
              tabIndex={0}
            >
              <h3>{content.title}</h3>
              <ChevronRightIcon
                className={css({
                  color: 'content.default.level-2',
                  transform: 'rotate(90deg)',
                  'details[open] &': { transform: 'rotate(-90deg)' },
                })}
              />
            </summary>
            <div
              className={css({
                textStyle: 'Text/M/Regular',
                color: 'content.default.level-3',
                mt: [12, 16],
                padding: '0 20px 16px',
                whiteSpace: 'pre-wrap',
              })}
            >
              {content.desc}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
};

export default Faq;
